'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { css } from '@lastbrand/web-ui-styled-system/css'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo, useState } from 'react'

import { NoReviewsDS } from '@src/components/blocks/no-reviews-ds'
import { ReviewCreateModalDS } from '@src/components/blocks/review-create-modal-ds/dynamic'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useLayoutContext, usePLPageMeta } from '@src/components/pl-page-context'
import { BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src'
import { CreateReview } from '@src/quince-components/src/atoms/reviews/createReview/createReview'
import { NoReviews } from '@src/quince-components/src/atoms/reviews/noReviews/NoReviews'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { REVIEWS_SOURCES } from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.constant'
import reviewSectionStyles from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/productReviews.module.scss'
import { TELEMETRY_EVENT_CATEGORIES } from '@src/telemetry/telemetry-events.constants'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { buildWriteReviewProduct } from '../shared/reviews.adapter'
import { EMPTY_PRODUCT_PROPERTIES } from '../shared/reviews.constants'
import { useIsBundlePdp } from '../shared/use-is-bundle-pdp'

import styles from './reviews-no-content.module.scss'

import type { NoContentData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

const noReviewsContainerCss = css({
  marginTop: 'space-32',
  marginBottom: 'space-48',
  marginX: { base: 'space-16', lg: '0' },
})

/**
 * REVIEWS_NO_CONTENT — the "No reviews yet" empty state. The BE sends this widget in place of the
 * reviews content when a product has none, so the section shows a real empty state (stars + heading +
 * WRITE A REVIEW + invitation) instead of an empty container frame.
 *
 * Reuses the legacy `NoReviews` atom + `CreateReview` popup, wired exactly like `reviews-highlight`:
 * the write-a-review popup only needs the product id (from `context.pdp.productIds.productId`) — no enrich
 * call; title/imageUrl come from page meta for the popup header. Mirrors the non-PL
 * `product-reviews-legacy` `reviewCount === 0` branch (outlined `write_a_review` button,
 * `first_review_invitation` message; the atom defaults the title to `no_reviews_yet`).
 */
function ReviewsNoContentWidgetComponent(_props: PLTypedWidgetProps<NoContentData>) {
  const { t } = useTranslation('common')
  const { isB2BSelfService } = useStorefrontModeContext()
  const [isCreateReviewOpen, setIsCreateReviewOpen] = useState(false)

  const layoutContext = useLayoutContext()
  const pageMeta = usePLPageMeta()
  const productId = layoutContext?.pdp?.productIds?.productId
  const writeReviewProduct = useMemo(
    () =>
      productId != null ? buildWriteReviewProduct(productId, pageMeta.title ?? '', pageMeta.seoData?.imageUrl) : null,
    [productId, pageMeta.title, pageMeta.seoData?.imageUrl],
  )

  const openCreateReviewPopUp = useCallback(() => {
    setIsCreateReviewOpen(true)
  }, [])

  // Bundles hide the write-a-review CTA + invitation — non-PL parity (`product-reviews-legacy`
  // passes `hideWriteReviewButton={product.type === PRODUCT_TYPES.BUNDLE …}` to NoReviews too).
  const hideWriteReviewButton = useIsBundlePdp()

  if (shouldUseDesignSystem()) {
    return (
      <div className={noReviewsContainerCss}>
        <NoReviewsDS
          openCreateReviewPopUp={openCreateReviewPopUp}
          writeReviewButton={{ children: t('write_a_review') }}
          trackConfig={{
            trackGA: true,
            trackInternal: true,
            category: TELEMETRY_EVENT_CATEGORIES.REVIEWS,
            action: 'click_write_review',
            context: { product_id: productId },
          }}
          message={t('first_review_invitation')}
          hideWriteReviewButton={hideWriteReviewButton}
        />
        {/* DS create-review modal (owns submission), same minimal product as the legacy branch. */}
        {writeReviewProduct && !hideWriteReviewButton && (
          <ReviewCreateModalDS
            show={isCreateReviewOpen}
            onCancel={() => {
              setIsCreateReviewOpen(false)
            }}
            product={writeReviewProduct}
            reviewSource={REVIEWS_SOURCES.PDP}
            isB2BSelfService={isB2BSelfService}
          />
        )}
      </div>
    )
  }

  return (
    // `.productNoReviews` gives the non-PL section spacing (margin: 3.2rem 0 4.8rem) so the empty
    // state's top/bottom spacing matches the legacy PDP; the NoReviews atom handles its own mobile
    // vs desktop font/padding.
    <div className={classNames(reviewSectionStyles.productNoReviews, styles.noContent)}>
      <NoReviews
        writeReviewButton={{
          type: BUTTON_TYPES.OUTLINED,
          size: BUTTON_SIZE.MEDIUM,
          children: t('write_a_review'),
        }}
        // Fire `click_write_review` (BUTTON_CLICK) on the legacy branch too — the atom only tracks
        // when trackConfig is present, and without this the event fired only on the DS branch.
        // Matches the DS branch above and the non-PL product-reviews-legacy empty state.
        trackConfig={{
          trackGA: true,
          trackInternal: true,
          category: TELEMETRY_EVENT_CATEGORIES.REVIEWS,
          action: 'click_write_review',
          context: { product_id: productId },
        }}
        message={t('first_review_invitation')}
        openCreateReviewPopUp={openCreateReviewPopUp}
        hideWriteReviewButton={hideWriteReviewButton}
      />
      {writeReviewProduct && !hideWriteReviewButton && (
        <CreateReview
          show={isCreateReviewOpen}
          onCancel={() => {
            setIsCreateReviewOpen(false)
          }}
          product={writeReviewProduct}
          reviewSource={REVIEWS_SOURCES.PDP}
          isB2BSelfService={isB2BSelfService}
          productProperties={EMPTY_PRODUCT_PROPERTIES}
        />
      )}
    </div>
  )
}

export const ReviewsNoContentWidget: PLTypedWidgetInterface<NoContentData, WidgetType.REVIEWS_NO_CONTENT> = {
  id: WidgetType.REVIEWS_NO_CONTENT,
  errorBoundary: PLBaseErrorBoundary,
  render: ReviewsNoContentWidgetComponent,
}
